"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
  shield: Shield, zap: Zap, lock: Lock, "eye-off": EyeOff,
  globe: Globe, server: Server, wifi: Wifi, key: Key,
  cpu: Cpu, "hard-drive": HardDrive, "refresh-cw": RefreshCw,
  "git-branch": GitBranch, cloud: Cloud, download: Download,
  monitor: Monitor, users: Users, "map-pin": MapPin,
  clock: Clock, layers: Layers, "check-circle": CheckCircle, eye: Eye,
};

interface Stat {
  value: string;
  label: string;
  icon?: string;
  description?: string;
}

interface StatsCardsProps {
  title?: string;
  stats: Stat[];
}

const StatsCards = ({ title, stats }: StatsCardsProps) => {
  if (!stats || stats.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-6xl mx-auto">
        {title && (
          <BlurFade delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground text-center mb-12">
              {title}
            </h2>
          </BlurFade>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, i) => {
            const Icon = stat.icon ? (ICON_MAP[stat.icon] || null) : null;
            return (
              <BlurFade key={i} delay={0.15 + i * 0.08}>
                <Card className="border-foreground/5 bg-white hover:shadow-lg transition-all h-full">
                  <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                    {Icon && (
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon size={24} className="text-primary" strokeWidth={1.5} />
                      </div>
                    )}
                    <span className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                      {stat.value}
                    </span>
                    <span className="text-sm font-semibold text-foreground/50 uppercase tracking-wider">
                      {stat.label}
                    </span>
                    {stat.description && (
                      <p className="text-xs text-foreground/40 leading-relaxed">
                        {stat.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsCards;
